import { useState, useCallback, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useHomeStore, useUserStore } from '@client/store';
import { useHandleCharacterFetchClick } from '@components'
import { AuthManager } from '@client/utility';

export const useHomepageAPI = () => {
  const { t } = useTranslation();
  const fetchCharactersFn = useHandleCharacterFetchClick();
  const [authLoading, setAuthLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [initialLoaded, setInitialLoaded] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();
  const pageParam = Number(searchParams.get('page') || 1);
  const searchQuery = searchParams.get('search') || '';

  const {
    currentPage,
    sortType,
    tag,
    gender,
    setCurrentPage,
    setSortType,
    setTag,
    setGender,
    setCharacters,
    setMaxPage,
    setLoading,
  } = useHomeStore();

  const includeSensitive = useUserStore((state) => state.include_sensitive);
  const setIsLogin = useUserStore((state) => state.setIsLogin);

  const hasFetchedRef = useRef(false);

  const doFetch = useCallback(
    async ({
      page,
      sort,
      tTag,
      gGender,
      query,
      includeSensitiveOverride,
    }: {
      page?: number;
      sort?: 'hot' | 'latest' | 'random' | 'tags';
      tTag?: string;
      gGender?: 'male' | 'female';
      query?: string;
      includeSensitiveOverride?: boolean;
    } = {}) => {
      if (isFetching) return;
      setIsFetching(true);
      setLoading(true);
      try {
        const state = useHomeStore.getState();
        const sensitiveValue = includeSensitiveOverride ?? includeSensitive;
        const res = await fetchCharactersFn(
          sort ?? state.sortType,
          20,
          page ?? state.currentPage,
          null,
          tTag ?? state.tag,
          gGender ?? state.gender,
          query ?? '',
          sensitiveValue
        );
        setCharacters(res.characters);
        setMaxPage(res.totalPages);
        setInitialLoaded(true);
      } catch {
        setCharacters([]);
        setMaxPage(1);
        setInitialLoaded(true);
      } finally {
        setIsFetching(false);
        setLoading(false);
      }
    },
    [fetchCharactersFn, isFetching, setCharacters, setLoading, setMaxPage, includeSensitive]
  );

  useEffect(() => {
    const initAuth = async () => {
      const loggedIn = await AuthManager.checkLogin();
      setIsLogin(loggedIn);
      setAuthLoading(false);
      if (!hasFetchedRef.current) {
        hasFetchedRef.current = true;
        if (pageParam !== currentPage) setCurrentPage(pageParam);
        await doFetch({ page: pageParam, query: searchQuery });
      }
      const unsub = AuthManager.reactiveLogin((loggedIn) => {
        setIsLogin(loggedIn);
        setAuthLoading(false);
      });
      return () => unsub?.();
    };
    initAuth();
  }, [doFetch, pageParam, searchQuery, currentPage, setCurrentPage, setIsLogin]);

  const handleCharacterFetchClick = useCallback(
    async (
      type: 'hot' | 'latest' | 'random' | 'tags',
      page: number = 1,
      options?: { tag?: string; gender?: 'male' | 'female' }
    ) => {
      setSortType(type);
      setCurrentPage(page);
      setSearchParams(
        { ...Object.fromEntries(searchParams.entries()), page: String(page) },
        { replace: true }
      );
      await doFetch({
        page,
        sort: type,
        tTag: options?.tag,
        gGender: options?.gender,
        query: searchQuery,
      });
    },
    [
      doFetch,
      searchQuery,
      setSortType,
      setCurrentPage,
      searchParams,
      setSearchParams,
    ]
  );

  const onButtonTagClicked = useCallback(
    async (tagValue: string, gGender?: 'male' | 'female') => {
      setTag(tagValue);
      setCurrentPage(1);
      setSearchParams(
        { ...Object.fromEntries(searchParams.entries()), page: '1' },
        { replace: true }
      );
      await doFetch({
        page: 1,
        sort: 'tags',
        tTag: tagValue,
        gGender,
        query: searchQuery,
      });
    },
    [
      doFetch,
      searchQuery,
      setTag,
      setCurrentPage,
      searchParams,
      setSearchParams,
    ]
  );

  const handleSearch = useCallback(
    async (query: string) => {
      setCurrentPage(1);
      setSearchParams(
        {
          ...Object.fromEntries(searchParams.entries()),
          page: '1',
          search: query,
        },
        { replace: true }
      );
      await doFetch({ page: 1, query });
    },
    [doFetch, setCurrentPage, searchParams, setSearchParams]
  );

  const setPage = useCallback(
    async (page: number) => {
      if (page < 1) return;
      setCurrentPage(page);
      setSearchParams(
        { ...Object.fromEntries(searchParams.entries()), page: String(page) },
        { replace: true }
      );
      await doFetch({ page, query: searchQuery });
    },
    [doFetch, setCurrentPage, searchParams, setSearchParams, searchQuery]
  );

  const refetchCharacters = useCallback(async () => {
    await doFetch({ page: currentPage, query: searchQuery });
  }, [doFetch, currentPage, searchQuery]);

  return {
    characters: useHomeStore((state) => state.characters),
    loading: useHomeStore((state) => state.loading),
    totalPages: useHomeStore((state) => state.maxPage),
    t,
    currentPage,
    sortType,
    tag,
    gender,
    handleCharacterFetchClick,
    onButtonTagClicked,
    handleSearch,
    setCurrentPage,
    setGender,
    setPage,
    authLoading,
    initialLoaded,
    refetchCharacters,
  };
}